/**
 * @file: useBluetoothGatewayDevices.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */
import { GatewayDeviceSource } from '@euler/model/entity';
import { useServiceFactory } from '@euler/services/factory';
import { usePersistFn, useRefreshableAsyncResource } from '@euler/utils/hooks';
import produce from 'immer';
import { Platform } from 'react-native';

const source: GatewayDeviceSource = Platform.OS;

export const useBluetoothGatewayDevices = () => {
  const { storeService } = useServiceFactory();
  const key = 'store:bluetooth-gateway-devices';
  const fetch = usePersistFn(() => {
    return storeService.listGatewayDevices(source);
  });

  const {
    value: devices,
    update,
    refresh,
    isRefreshing,
  } = useRefreshableAsyncResource(fetch, key);

  const updateAsync = usePersistFn(
    async (
      deviceId: string,
      params: {
        name: string;
      },
    ) => {
      const updated = await storeService.updateGatewayDeviceName(
        source,
        deviceId,
        params.name,
      );
      const result = produce(devices, draft => {
        const index = draft.findIndex(x => x.deviceId === deviceId);
        if (index < 0) {
          console.warn(
            'cannot find bluetooth gateway device: ',
            deviceId,
            ', this must be an error',
          );
          return;
        }
        draft[index] = updated;
      });
      update(result);
    },
  );

  const createAsync = usePersistFn(
    async (params: { deviceId: string; name?: string }) => {
      const created = await storeService.registerGatewayDevice({
        source,
        ...params,
      });
      const result = produce(devices, draft => {
        draft.push(created);
      });
      update(result);
    },
  );

  const deleteAsync = usePersistFn(async (deviceId: string) => {
    await storeService.removeGatewayDevice(source, deviceId);
    const result = produce(devices, draft => {
      const index = draft.findIndex(x => x.deviceId === deviceId);
      if (index < 0) {
        console.warn(
          'cannot find bluetooth gateway device: ',
          deviceId,
          ', this must be an error',
        );
        return;
      }
      draft.splice(index, 1);
    });
    update(result);
  });

  return {
    devices,
    refresh,
    isRefreshing,
    createAsync,
    updateAsync,
    deleteAsync,
  };
};

export const useBluetoothGatewayDeviceCount = () => {
  const { devices } = useBluetoothGatewayDevices();
  return devices.length;
};
